import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";
import { GlobalService } from "../../modules/shared/services/global.service";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import i18n from "../../i18n";

// ==================== INTERFACES ====================

export interface MonitoringConfig {
  id: string;
  configKey: string;
  consecutiveVisitsWarningThreshold: number;
  totalVisitsForConversionCheck: number;
  minimumContractsRequired: number;
  categoryRevenueThresholds: Record<string, number>;
  consecutiveVideoCallsWarningThreshold: number;
  totalVideoCallsForConversionCheck: number;
  minimumConversionRatePercent: number;
  minimumCompletionRatePercent: number;
  reportsForFirstWarning: number;
  reportsForSuspension: number;
  reportsForPermanentBan: number;
  contactSharingForFirstWarning: number;
  contactSharingForSuspension: number;
  contactSharingForBan: number;
  consecutiveRefundRequestsForSuspension: number;
  firstSuspensionDays: number;
  secondSuspensionDays: number;
  pointsDeductedPerWarning: number;
  trustBonusPoints: number;
  compliantMonthsForBonus: number;
  warningRepeatDays: number;
  monthlyResetDays: number;
  isActive: boolean;
}

export interface ProfessionalMonitoring {
  id: string;
  userId: string;
  professionalType: string;
  totalHomeVisits: number;
  consecutiveVisitsWithoutContract: number;
  totalVisitsInPeriod: number;
  contractsClosedInPeriod: number;
  totalRevenueThisMonth: number;
  totalVideoCalls: number;
  consecutiveVideoCallsWithoutContract: number;
  videoCallsInPeriod: number;
  videoContractsClosedInPeriod: number;
  contractCompletionRate: number;
  totalWarnings: number;
  contactSharingAttempts: number;
  confirmedReports: number;
  anomalyCount: number;
  totalRefundRequests: number;
  consecutiveRefundRequests: number;
  refundRequestsByClient: Record<string, number>;
  trustScore: number;
  isSuspended: boolean;
  suspensionStartDate?: Date;
  suspensionEndDate?: Date;
  suspensionReason?: string;
  isPermanentlyBanned: boolean;
  banReason?: string;
  chatRestricted: boolean;
  videoRestricted: boolean;
  restrictionEndDate?: Date;
  compliantMonthsInRow: number;
  performanceTier: string;
  tierScore: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface WarningPopup {
  id: string;
  userId: string;
  warningType: string;
  message: string;
  severity: string;
  isDismissed: boolean;
  dismissedAt?: Date;
  nextShowDate?: Date;
  hasBeenShown: boolean;
  shownAt?: Date;
  pointsDeducted?: number;
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface CustomerReport {
  id: string;
  professionalId: string;
  clientId: string;
  contractId?: string;
  jobId?: string;
  reportType: string;
  description: string;
  status: string;
  adminNotes?: string;
  reviewedBy?: string;
  reviewedAt?: Date;
  evidence?: string[];
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

// ==================== RESTRICTIONS ====================

export const checkCanCommunicate = createAsyncThunk<
  {
    canCommunicate: boolean;
    reason: string | null;
    suspensionType: string;
    suspensionEndDate?: string;
    isExistingClient?: boolean;
  },
  { professionalId: string; clientId: string },
  { rejectValue: string }
>(
  "professionalMonitoring/canCommunicate",
  async ({ professionalId, clientId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await HttpService.get(
        `api/v1/professional-monitoring/can-communicate/${professionalId}/${clientId}`,
        true
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to check communication permission"
      );
    }
  }
);

// ==================== CONFIG ACTIONS ====================

export const getMonitoringConfig = createAsyncThunk<
  MonitoringConfig,
  string,
  { rejectValue: string }
>("professionalMonitoring/getConfig", async (configKey, { rejectWithValue, dispatch }) => {
  try {
    const response = await HttpService.get(`api/v1/professional-monitoring/config/${configKey}`, true);
    return response;
  } catch (error: any) {
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.configFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.configFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch configuration"
    );
  }
});

export const getAllMonitoringConfigs = createAsyncThunk<
  MonitoringConfig[],
  void,
  { rejectValue: string }
>("professionalMonitoring/getAllConfigs", async (_, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.get("api/v1/professional-monitoring/config", true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.configsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.configsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch configurations"
    );
  }
});

export const updateMonitoringConfig = createAsyncThunk<
  MonitoringConfig,
  { configKey: string; data: Partial<MonitoringConfig> },
  { rejectValue: string }
>("professionalMonitoring/updateConfig", async ({ configKey, data }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.patch(
      `api/v1/professional-monitoring/config/${configKey}`,
      data,
      true
    );
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.monitoring.configUpdated"),
      i18n.t("toast.monitoring.configUpdatedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.configUpdateFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.configUpdateFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to update configuration"
    );
  }
});

// ==================== MONITORING DATA ACTIONS ====================

export const getMonitoringData = createAsyncThunk<
  ProfessionalMonitoring,
  string,
  { rejectValue: string }
>("professionalMonitoring/getData", async (userId, { rejectWithValue }) => {
  try {
    const response = await HttpService.get(`api/v1/professional-monitoring/user/${userId}`, true);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch monitoring data"
    );
  }
});

export const getAllMonitoringData = createAsyncThunk<
  { data: ProfessionalMonitoring[]; pagination: any },
  { isSuspended?: boolean; isPermanentlyBanned?: boolean; performanceTier?: string; page?: number; limit?: number } | undefined,
  { rejectValue: string }
>("professionalMonitoring/getAllData", async (params = {}, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const queryParams = new URLSearchParams();
    if (params.isSuspended !== undefined) queryParams.append("isSuspended", params.isSuspended.toString());
    if (params.isPermanentlyBanned !== undefined) queryParams.append("isPermanentlyBanned", params.isPermanentlyBanned.toString());
    if (params.performanceTier) queryParams.append("performanceTier", params.performanceTier);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    
    const url = `api/v1/professional-monitoring${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await HttpService.get(url, true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.dataFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.dataFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch monitoring data"
    );
  }
});

export const trackActivity = createAsyncThunk<
  ProfessionalMonitoring,
  {
    professionalId: string;
    activityType: string;
    contractId?: string;
    clientId?: string;
    amount?: number;
    metadata?: Record<string, any>;
  },
  { rejectValue: string }
>("professionalMonitoring/trackActivity", async (data, { rejectWithValue }) => {
  try {
    console.log("🚀 [REDUX] trackActivity action called with:", JSON.stringify(data, null, 2));
    const response = await HttpService.post("api/v1/professional-monitoring/track-activity", data, true);
    console.log("✅ [REDUX] trackActivity response received:", response);
    return response;
  } catch (error: any) {
    console.error("❌ [REDUX] trackActivity failed:", error?.response?.data || error.message);
    return rejectWithValue(
      error?.response?.data?.message || "Failed to track activity"
    );
  }
});

// ==================== WARNING ACTIONS ====================

export const getPendingWarnings = createAsyncThunk<
  WarningPopup[],
  string,
  { rejectValue: string }
>("professionalMonitoring/getPendingWarnings", async (userId, { rejectWithValue }) => {
  try {
    const response = await HttpService.get(`api/v1/professional-monitoring/warnings/${userId}/pending`, true);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch warnings"
    );
  }
});

export const getAllWarnings = createAsyncThunk<
  WarningPopup[],
  string,
  { rejectValue: string }
>("professionalMonitoring/getAllWarnings", async (userId, { rejectWithValue }) => {
  try {
    const response = await HttpService.get(`api/v1/professional-monitoring/warnings/${userId}`, true);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch warnings"
    );
  }
});

export const dismissWarning = createAsyncThunk<
  WarningPopup,
  string,
  { rejectValue: string }
>("professionalMonitoring/dismissWarning", async (warningId, { rejectWithValue }) => {
  try {
    const response = await HttpService.patch(
      "api/v1/professional-monitoring/warnings/dismiss",
      { warningId },
      true
    );
    GlobalService.showToast("success", i18n.t("toast.monitoring.warningDismissed"), i18n.t("toast.monitoring.warningDismissedMessage"));
    return response;
  } catch (error: any) {
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.dismissWarningFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.dismissWarningFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to dismiss warning"
    );
  }
});

// ==================== REPORT ACTIONS ====================

export const createReport = createAsyncThunk<
  CustomerReport,
  {
    professionalId: string;
    clientId: string;
    reportType: string;
    description: string;
    contractId?: string;
    jobId?: string;
    evidence?: string[];
  },
  { rejectValue: string }
>("professionalMonitoring/createReport", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.post("api/v1/professional-monitoring/reports", data, true);
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.monitoring.reportSubmitted"),
      i18n.t("toast.monitoring.reportSubmittedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.reportSubmitFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.reportSubmitFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to submit report"
    );
  }
});

export const getReportsByProfessional = createAsyncThunk<
  CustomerReport[],
  { professionalId: string; status?: string },
  { rejectValue: string }
>("professionalMonitoring/getReportsByProfessional", async ({ professionalId, status }, { rejectWithValue }) => {
  try {
    const url = `api/v1/professional-monitoring/reports/professional/${professionalId}${status ? `?status=${status}` : ""}`;
    const response = await HttpService.get(url, true);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch reports"
    );
  }
});

export const getReportsByClient = createAsyncThunk<
  CustomerReport[],
  string,
  { rejectValue: string }
>("professionalMonitoring/getReportsByClient", async (clientId, { rejectWithValue }) => {
  try {
    const response = await HttpService.get(`api/v1/professional-monitoring/reports/client/${clientId}`, true);
    return response;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch reports"
    );
  }
});

export const getAllReports = createAsyncThunk<
  { data: CustomerReport[]; pagination: any },
  { status?: string; reportType?: string; page?: number; limit?: number } | undefined,
  { rejectValue: string }
>("professionalMonitoring/getAllReports", async (params = {}, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.reportType) queryParams.append("reportType", params.reportType);
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    
    const url = `api/v1/professional-monitoring/reports${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await HttpService.get(url, true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.reportsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.reportsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch reports"
    );
  }
});

export const reviewReport = createAsyncThunk<
  CustomerReport,
  {
    reportId: string;
    status: string;
    adminNotes?: string;
    reviewedBy: string;
  },
  { rejectValue: string }
>("professionalMonitoring/reviewReport", async ({ reportId, ...data }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.patch(
      `api/v1/professional-monitoring/reports/${reportId}/review`,
      data,
      true
    );
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.monitoring.reportReviewed"),
      i18n.t("toast.monitoring.reportReviewedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.reportReviewFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.reportReviewFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to review report"
    );
  }
});

// ==================== ACCOUNT MANAGEMENT ====================

export const reactivateAccount = createAsyncThunk<
  { success: boolean; message: string },
  string,
  { rejectValue: string }
>("professionalMonitoring/reactivateAccount", async (userId, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.post(
      `api/v1/professional-monitoring/reactivate/${userId}`,
      {},
      true
    );
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.monitoring.accountReactivated"),
      i18n.t("toast.monitoring.accountReactivatedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.monitoring.accountReactivateFailed"),
      error?.response?.data?.message || i18n.t("toast.monitoring.accountReactivateFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to reactivate account"
    );
  }
});

